import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import {
  useGetPublicCheckout,
  usePayPublicCheckout,
  useRequestPublicCheckoutOtp,
  useSubmitPublicCustomerInfo,
  useVerifyPublicCheckoutOtp,
} from "@/api";
import type { CustomerInfoDtoShippingMethod } from "@/api/schemas/customerInfoDtoShippingMethod";
import type { LatLng } from "@/components/Molecules/LocationPicker";
import { useAddressAutofill } from "@/hooks/useAddressAutofill";
import { useOtpTimer } from "@/hooks/useOtpTimer";
import { getApiErrorMessage, isOnlinePaymentDisabledError } from "@/lib/api/errors";
import {
  checkoutCustomerSchema,
  checkoutPhoneSchema,
  type CheckoutCustomerFormValues,
  type CheckoutPhoneFormValues,
} from "@/lib/validation/checkout.schemas";

export type CheckoutPhase = "form" | "processing" | "success";

/** Customer-facing steps before the bank gateway. */
export type CheckoutStep = "phone" | "otp" | "details" | "payment";

export const CHECKOUT_STEPS: CheckoutStep[] = ["phone", "otp", "details", "payment"];

const OTP_LENGTH = 6;

export function useCheckoutPage(token: string) {
  const [phase, setPhase] = useState<CheckoutPhase>("form");
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [devOtpCode, setDevOtpCode] = useState<string>();
  const [isChangingPhone, setIsChangingPhone] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  // undefined = not touched yet, so the saved location (if any) is shown.
  const [pickedLocation, setPickedLocation] = useState<LatLng>();
  const [pickedShippingMethod, setPickedShippingMethod] = useState<CustomerInfoDtoShippingMethod>();
  const otpTimer = useOtpTimer(0);

  const { data, isLoading, isError, error, refetch } = useGetPublicCheckout(token);
  const checkout = data?.status === 200 ? data.data.data : undefined;
  const verifiedPhone = checkout?.verified_phone;

  // Only the methods the store enabled; the earlier choice (or the first one) is preselected.
  const shippingMethods = (checkout?.shipping_methods ?? []) as CustomerInfoDtoShippingMethod[];
  const savedShippingMethod = checkout?.shipping_method as CustomerInfoDtoShippingMethod | undefined;
  const shippingMethod =
    [pickedShippingMethod, savedShippingMethod].find((method) => method && shippingMethods.includes(method)) ??
    shippingMethods[0];

  const phoneForm = useForm<CheckoutPhoneFormValues>({
    resolver: zodResolver(checkoutPhoneSchema),
    defaultValues: { phone_number: "" },
  });

  const form = useForm<CheckoutCustomerFormValues>({
    resolver: zodResolver(checkoutCustomerSchema),
    defaultValues: {
      full_name: "",
      province: "",
      city: "",
      address: "",
      postal_code: "",
    },
  });

  // Pre-fill the details form from what this customer entered before.
  const savedCustomer = checkout?.customer;
  useEffect(() => {
    if (!savedCustomer) return;
    form.reset({
      full_name: savedCustomer.full_name,
      province: savedCustomer.province,
      city: savedCustomer.city,
      address: savedCustomer.address,
      postal_code: savedCustomer.postal_code,
    });
  }, [savedCustomer, form]);

  const savedLocation: LatLng | null =
    savedCustomer?.latitude != null && savedCustomer.longitude != null
      ? { latitude: savedCustomer.latitude, longitude: savedCustomer.longitude }
      : null;
  const location = pickedLocation ?? savedLocation;

  // Values set programmatically (map autofill) do not reach MUI, so labels shrink from these.
  const [watchedProvince, watchedCity, watchedAddress] = useWatch({
    control: form.control,
    name: ["province", "city", "address"],
  });
  const filledFields = {
    province: Boolean(watchedProvince),
    city: Boolean(watchedCity),
    address: Boolean(watchedAddress),
  };

  const addressAutofill = useAddressAutofill(token, form);
  const { lookup: lookupAddress } = addressAutofill;
  const onLocationChange = useCallback(
    (next: LatLng) => {
      setPickedLocation(next);
      void lookupAddress(next);
    },
    [lookupAddress],
  );

  const step: CheckoutStep =
    !verifiedPhone || isChangingPhone
      ? otpSentTo
        ? "otp"
        : "phone"
      : checkout?.customer_info_required || isEditingDetails
        ? "details"
        : "payment";

  const failWith = useCallback((err: unknown, fallback: string) => {
    setStepError(getApiErrorMessage(err) ?? fallback);
  }, []);

  const { mutate: requestOtp, isPending: isRequestingOtp } = useRequestPublicCheckoutOtp({
    mutation: {
      onSuccess: (res, variables) => {
        if (res.status !== 200) return;
        setOtpSentTo(variables.data.phone_number);
        setOtpCode("");
        setDevOtpCode(res.data.data.code);
        otpTimer.reset(res.data.data.resend_after);
      },
      onError: (err) => failWith(err, "ارسال کد تأیید انجام نشد. دوباره تلاش کنید."),
    },
  });

  const { mutate: verifyOtp, isPending: isVerifyingOtp } = useVerifyPublicCheckoutOtp({
    mutation: {
      onSuccess: (res) => {
        if (res.status !== 200) return;
        setIsChangingPhone(false);
        setOtpSentTo(null);
        setOtpCode("");
        setDevOtpCode(undefined);
        void refetch();
      },
      onError: (err) => {
        setOtpCode("");
        failWith(err, "کد تأیید درست نیست. دوباره تلاش کنید.");
      },
    },
  });

  const { mutate: submitCustomer, isPending: isSubmitting } = useSubmitPublicCustomerInfo({
    mutation: {
      onSuccess: () => {
        setIsEditingDetails(false);
        void refetch();
      },
      onError: (err) => failWith(err, "ذخیره اطلاعات ارسال انجام نشد. دوباره تلاش کنید."),
    },
  });

  const {
    mutate: payCheckout,
    isPending: isPaying,
    error: payError,
  } = usePayPublicCheckout({
    mutation: {
      onSuccess: (res) => {
        if (res.status !== 200) return;
        const redirectUrl = res.data.data.redirect_url;
        if (redirectUrl) {
          setPhase("processing");
          window.location.href = redirectUrl;
          return;
        }
        setPhase("success");
      },
      onError: (err) => {
        if (!isOnlinePaymentDisabledError(err)) {
          failWith(err, "اتصال به درگاه پرداخت انجام نشد. دوباره تلاش کنید.");
        }
      },
    },
  });

  const onSubmitPhone = phoneForm.handleSubmit((values) => {
    setStepError(null);
    requestOtp({ token, data: { phone_number: values.phone_number } });
  });

  const onResendOtp = useCallback(() => {
    if (!otpSentTo || !otpTimer.isExpired) return;
    setStepError(null);
    requestOtp({ token, data: { phone_number: otpSentTo } });
  }, [otpSentTo, otpTimer.isExpired, requestOtp, token]);

  const onOtpChange = useCallback(
    (value: string) => {
      setOtpCode(value);
      setStepError(null);
      if (otpSentTo && value.length === OTP_LENGTH) {
        verifyOtp({ token, data: { phone_number: otpSentTo, code: value } });
      }
    },
    [otpSentTo, token, verifyOtp],
  );

  const onEditPhone = useCallback(() => {
    setStepError(null);
    setOtpSentTo(null);
    setOtpCode("");
    setIsChangingPhone(true);
    if (verifiedPhone) phoneForm.setValue("phone_number", verifiedPhone);
  }, [phoneForm, verifiedPhone]);

  const onEditDetails = useCallback(() => {
    setStepError(null);
    setIsEditingDetails(true);
  }, []);

  const onSubmitCustomer = form.handleSubmit((values) => {
    if (!verifiedPhone) return;
    setStepError(null);
    submitCustomer({
      token,
      data: {
        ...values,
        phone_number: verifiedPhone,
        shipping_method: shippingMethod,
        ...(location ?? {}),
      },
    });
  });

  const handlePay = () => {
    setStepError(null);
    payCheckout({ token });
  };

  const storeInitial = checkout?.store.name.trim().charAt(0) || "ف";
  const isPaid = checkout?.payment_status === "paid" || phase === "success";
  const isProcessing = phase === "processing" || isPaying;
  const isUnavailable =
    isOnlinePaymentDisabledError(error) || isOnlinePaymentDisabledError(payError);

  return {
    checkout,
    form,
    phoneForm,
    phase,
    step,
    stepIndex: CHECKOUT_STEPS.indexOf(step),
    stepError,
    isLoading,
    isError,
    isUnavailable,
    isSubmitting,
    isPaying,
    isPaid,
    isProcessing,
    isRequestingOtp,
    isVerifyingOtp,
    otpSentTo,
    otpCode,
    otpLength: OTP_LENGTH,
    devOtpCode,
    resendSeconds: otpTimer.seconds,
    canResendOtp: otpTimer.isExpired && !isRequestingOtp,
    verifiedPhone,
    savedCustomer,
    location,
    onLocationChange,
    shippingMethods,
    shippingMethod,
    savedShippingMethod,
    onShippingMethodChange: setPickedShippingMethod,
    filledFields,
    geocodeStatus: addressAutofill.status,
    addressSuggestion: addressAutofill.suggestion,
    onApplyAddressSuggestion: addressAutofill.applySuggestion,
    onDismissAddressSuggestion: addressAutofill.dismissSuggestion,
    canCancelPhoneChange: isChangingPhone && Boolean(verifiedPhone),
    canCancelDetailsEdit: isEditingDetails && !checkout?.customer_info_required,
    showMobilePayDock: step === "payment",
    storeInitial,
    onSubmitPhone,
    onResendOtp,
    onOtpChange,
    onEditPhone,
    onCancelPhoneChange: () => {
      setIsChangingPhone(false);
      setOtpSentTo(null);
      setStepError(null);
    },
    onEditDetails,
    onCancelDetailsEdit: () => setIsEditingDetails(false),
    onSubmitCustomer,
    handlePay,
  };
}
